import { useEffect, useMemo, useState } from "react";
import { api } from "../../api/apiClient";
import type { SalePeriodTotals } from "../../api/types";
import { businessToday } from "../../utils/businessDay";
import { StatTile } from "../ui/StatTile";

/**
 * G-135 (OD-174): a manager's Finances opens on what their shop sold — today
 * and this month, in Barcelona days (`/sales/totals` reads both ends inclusive
 * and is already scoped to the manager's shop by the server). G-169: the
 * amounts are the server's, by the same rule the Sales records page reads.
 */

const euro = (n: number) => `€${n.toFixed(2)}`;

function ticketsLabel(n: number): string {
  return `${n} ${n === 1 ? "ticket" : "tickets"}`;
}

export function ShopSalesTiles() {
  const today = useMemo(() => businessToday(), []);
  const monthFrom = `${today.year}-${String(today.month).padStart(2, "0")}-01`;
  const [day, setDay] = useState<SalePeriodTotals | null>(null);
  const [month, setMonth] = useState<SalePeriodTotals | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // Two periods of whole Barcelona days (G-162: refunds come off on
        // the day they were given, a removal comes back on its own day).
        const [dayTotals, monthTotals] = await Promise.all([
          api.getSalesTotals(undefined, { from: today.ymd, to: today.ymd }),
          api.getSalesTotals(undefined, { from: monthFrom, to: today.ymd }),
        ]);
        if (cancelled) return;
        setDay(dayTotals);
        setMonth(monthTotals);
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not load sales");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [today, monthFrom]);

  const monthName = new Date(Date.UTC(today.year, today.month - 1, 1)).toLocaleString(undefined, {
    month: "short",
    timeZone: "UTC",
  });

  const dayRange = today.day === 1 ? "1" : `1–${today.day}`;

  return (
    <>
      {error ? <div className="alert error">{error}</div> : null}
      <div className="stat-tile-row">
        <StatTile
          label="Revenue today"
          value={day ? euro(Number(day.revenueEur)) : "…"}
          tone="positive"
          hint={day ? ticketsLabel(day.tickets) : undefined}
        />
        <StatTile
          label="Revenue this month"
          value={month ? euro(Number(month.revenueEur)) : "…"}
          hint={month ? `${ticketsLabel(month.tickets)} · ${dayRange} ${monthName}` : undefined}
        />
      </div>
    </>
  );
}
